import React, { createContext, useContext, useEffect, useReducer } from "react";

import { contextConstants } from "context/constants";
import { contextReducers } from "context/reducers";
import { contextActions } from "context/actions";
import { JobsContext } from "./JobsProvider";

const STATUSES_KEYS_LINKING = {
  SHORTLISTED: "isShortlisting",
  MATCHED: "isMatching",
  DELETED: "isDeleting",
  SKIPPED: "isSkipping",
};

export const MatchContext = createContext();

export const MatchProvider = ({ children }) => {
  const { jobOpp, initJob } = useContext(JobsContext);

  const [state, dispatch] = useReducer(
    contextReducers.match.reducer,
    contextReducers.match.initialState
  );

  const initMatches = () => {
    const matches = contextActions.match.initMatches(jobOpp);

    dispatch({
      type: contextConstants.match.MATCHES_LOADED,
      payload: { matches },
    });
  };

  const initBestFit = async () => {
    const bestFit = await contextActions.match.initBestFit(jobOpp);

    dispatch({
      type: contextConstants.match.BEST_FIT_LOADED,
      payload: { bestFit },
    });
  };

  const updateMatchFieldLocally = (applicationId, fieldKey, value) => {
    const matches = contextActions.match.updateMatchFieldLocally(
      applicationId,
      fieldKey,
      value,
      state.matches
    );

    const updatedMatch = matches.find(
      (el) => el.applicationId === applicationId
    );

    dispatch({
      type: contextConstants.match.MATCHES_LOADED,
      payload: { matches },
    });

    return updatedMatch;
  };

  const updateFitFieldLocally = (id, fieldKey, value) => {
    const bestFit = contextActions.match.updateFitFieldLocally(
      id,
      fieldKey,
      value,
      state.bestFit
    );

    const updatedFit = bestFit.find((el) => el.id === id);

    dispatch({
      type: contextConstants.match.BEST_FIT_LOADED,
      payload: { bestFit },
    });

    return updatedFit;
  };

  const removeMatchLocally = (applicationId) => {
    const matches = contextActions.match.removeMatchLocally(
      applicationId,
      state.matches
    );

    dispatch({
      type: contextConstants.match.MATCHES_LOADED,
      payload: { matches },
    });
  };

  const replaceMatchLocally = (match) => {
    const matches = contextActions.match.replaceMatchLocally(
      match,
      state.matches
    );

    dispatch({
      type: contextConstants.match.MATCHES_LOADED,
      payload: { matches },
    });
  };

  const createMatch = async (applicationId, jobOpportunityId, status) => {
    try {
      await contextActions.match.createMatch(
        applicationId,
        jobOpportunityId,
        status
      );

      await initJob(jobOpp.id);
    } catch (error) {
      console.log("callMatchAction error: ", error);
    }
  };

  const updateMatch = async (applicationId, jobOpportunityId, args) => {
    try {
      const existingMatch = state.matches.find(
        (el) => el.applicationId === applicationId
      );

      await contextActions.match.updateMatch({
        applicationId,
        jobOpportunityId,
        ...args,
      });

      const params = {
        ...existingMatch,
        ...args,
      };
      if (args.status) {
        params[STATUSES_KEYS_LINKING[args.status]] = false;
      }

      replaceMatchLocally(params);
    } catch (error) {
      console.log("callMatchAction error: ", error);
    }
  };

  const removeMatch = async (applicationId, jobOpportunityId) => {
    try {
      await contextActions.match.removeMatch(applicationId, jobOpportunityId);

      removeMatchLocally(applicationId);
    } catch (error) {
      console.log("removeMatch error: ", error);
    }
  };

  const clearMatches = () => {
    dispatch({
      type: contextConstants.match.MATCHES_LOADED,
      payload: { matches: [] },
    });

    dispatch({
      type: contextConstants.match.BEST_FIT_LOADED,
      payload: { bestFit: [] },
    });
  };

  const match = {
    ...state,
    initMatches,
    createMatch,
    updateMatch,
    removeMatch,
    updateMatchFieldLocally,
    updateFitFieldLocally,
    removeMatchLocally,
    replaceMatchLocally,
    clearMatches,
  };

  useEffect(() => {
    if (jobOpp.id) {
      initMatches();
      initBestFit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobOpp]);

  return (
    <MatchContext.Provider value={match}>{children}</MatchContext.Provider>
  );
};
